"use client";

import { Table } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { format } from "date-fns";

import { useWorkerPointHistories } from "@/components/model/user/_hooks/useWorkerPointHistories";
import {
  Button,
  Center,
  Group,
  H2,
  Loader,
  Modal,
  Stack,
  Text,
} from "@/components/ui/elements";

type Props = {
  opened: boolean;
  close: () => void;
  points: number;
};

export function PointHistoryModal({ opened, close, points }: Props) {
  const isMobile = useMediaQuery("(max-width: 50em)");
  const {
    edges,
    hasNextPage,
    loading,
    error,
    isFetchingMore,
    fetchMoreHistories,
  } = useWorkerPointHistories({ skip: !opened });

  return (
    <Modal opened={opened} close={close} fullScreen={isMobile}>
      <Stack py="md">
        <H2>【持ち点 変動履歴】</H2>
        <Group gap={4}>
          <Text>現在の持ち点：</Text>
          <Text fw="bold" c="teal">
            {points}pt
          </Text>
        </Group>

        {loading ? (
          <Center>
            <Loader />
          </Center>
        ) : error ? (
          <Text c="red" ta="center">
            持ち点変動履歴の取得に失敗しました。時間をおいて再度お試しください。
          </Text>
        ) : edges.length > 0 ? (
          <>
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>発生日時</Table.Th>
                  <Table.Th>理由</Table.Th>
                  <Table.Th ta="center">増減</Table.Th>
                  <Table.Th ta="center">残高</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {edges.map((edge) => {
                  const node = edge?.node;
                  if (!node) return null;
                  return (
                    <Table.Tr key={node.id}>
                      <Table.Td>
                        {format(new Date(node.createdAt), "yyyy/MM/dd HH:mm")}
                      </Table.Td>
                      <Table.Td>{node.displayLabel}</Table.Td>
                      <Table.Td ta="center">
                        <Text
                          c={node.pointsChange > 0 ? "teal" : "red"}
                          fw="bold"
                        >
                          {node.pointsChange > 0
                            ? `+${node.pointsChange}`
                            : node.pointsChange}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="center">{node.balanceAfter}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            {hasNextPage && (
              <Button
                variant="outline"
                color="teal"
                radius="xl"
                onClick={fetchMoreHistories}
                fullWidth
                loading={isFetchingMore}
                disabled={isFetchingMore}
              >
                もっと見る
              </Button>
            )}
          </>
        ) : (
          <Text c="dimmed" ta="center">
            変動履歴はまだありません
          </Text>
        )}
      </Stack>
    </Modal>
  );
}
